import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import NotFound from "../ul/NotFound";

// Register the required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Define theme colors
const THEME = {
  views: {
    primary: "#3b82f6", // blue-500
    secondary: "#93c5fd", // blue-300
    gradient: ["rgba(59, 130, 246, 0.15)", "rgba(59, 130, 246, 0.01)"],
    pointBorder: "#ffffff",
  },
  clicks: {
    primary: "#8b5cf6", // violet-500
    secondary: "#c4b5fd", // violet-300
    gradient: ["rgba(139, 92, 246, 0.15)", "rgba(139, 92, 246, 0.01)"],
    pointBorder: "#ffffff",
  },
};

// Format date for display
const formatDateForDisplay = (dateString) => {
  const options = { month: "short", day: "numeric" };
  return new Date(dateString.split("/").reverse().join("-")).toLocaleDateString(
    "en-US",
    options
  );
};

// Generate chart data from raw data
const generateChartData = (data, label, theme) => {
  if (!data || data.length === 0) return null;

  // Aggregate counts by date
  const counts = data.reduce((acc, entry) => {
    const date = new Date(entry.createdAt);
    const day = date.toLocaleDateString("en-GB"); // Format: dd/MM/yyyy
    acc[day] = (acc[day] || 0) + entry.count;
    return acc;
  }, {});

  // Sort data by date
  const sortedData = Object.entries(counts)
    .map(([date, count]) => ({ x: date, y: count }))
    .sort(
      (a, b) =>
        new Date(a.x.split("/").reverse().join("-")) -
        new Date(b.x.split("/").reverse().join("-"))
    );

  // Create formatted labels for display
  const formattedLabels = sortedData.map((entry) =>
    formatDateForDisplay(entry.x)
  );

  // Create gradient for background
  const getGradient = (ctx) => {
    if (!ctx) return theme.gradient[0];

    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, theme.gradient[0]);
    gradient.addColorStop(1, theme.gradient[1]);
    return gradient;
  };

  return {
    labels: formattedLabels,
    rawDates: sortedData.map((entry) => entry.x),
    datasets: [
      {
        label,
        data: sortedData.map((entry) => entry.y),
        borderColor: theme.primary,
        borderWidth: 2.5,
        pointBackgroundColor: theme.primary,
        pointBorderColor: theme.pointBorder,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: theme.primary,
        pointHoverBorderColor: theme.pointBorder,
        pointHoverBorderWidth: 3,
        backgroundColor: function (context) {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;
          return getGradient(ctx);
        },
        fill: "start",
        tension: 0.3,
      },
    ],
  };
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label, color, rawDates }) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const dateIndex = payload[0].dataIndex;
    const rawDate = rawDates[dateIndex];

    return (
      <div className="custom-tooltip bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="text-sm text-gray-600 mb-1">
          {formatDateForDisplay(rawDate)}
        </p>
        <p className="font-bold" style={{ color }}>
          {value.toLocaleString()}{" "}
          {value === 1 ? "interaction" : "interactions"}
        </p>
      </div>
    );
  }
  return null;
};

// Chart component that handles data visualization
const ChartComponent = ({ data, label, chartType }) => {
  const theme = chartType === "views" ? THEME.views : THEME.clicks;
  const chartData = generateChartData(data, label, theme);

  if (!chartData) return <NotFound />;

  // Extract only the values for max and min calculations
  const values = chartData.datasets[0].data;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const isZeroLine = min === 0 && max === 0;

  // Set suggested min and max for y-axis based on data range
  let suggestedMin = isZeroLine ? 0 : Math.max(0, min - (max - min) * 0.1);
  let suggestedMax = isZeroLine ? 10 : max + (max - min) * 0.1;

  return (
    <div className="w-full h-full">
      <div className="bg-gradient-to-br from-white to-gray-50 p-4 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 h-full flex flex-col">
        {/* Chart Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <div
              className="w-2 h-6 rounded-full mr-2"
              style={{ backgroundColor: theme.primary }}
            ></div>
            <h3 className="text-base font-semibold text-gray-800">
              {label} Trend
            </h3>
          </div>
          <div
            className="text-xs font-medium py-1 px-2 rounded-full"
            style={{
              backgroundColor: `${theme.primary}20`,
              color: theme.primary,
            }}
          >
            {values.reduce((a, b) => a + b, 0).toLocaleString()} Total
          </div>
        </div>

        {/* Chart Container - Key fixes for responsiveness */}
        <div className="flex-grow relative">
          <div className="absolute inset-0">
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: "index",
                  intersect: false,
                },
                elements: {
                  line: {
                    tension: 0.3,
                    borderWidth: 2.5,
                    borderJoinStyle: "round",
                    capBezierPoints: true,
                  },
                  point: {
                    hitRadius: 8,
                  },
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    enabled: true,
                    backgroundColor: "#ffffff",
                    titleColor: "#333",
                    bodyColor: "#000",
                    borderColor: theme.primary,
                    borderWidth: 1,
                    padding: 10,
                    cornerRadius: 8,
                    titleFont: {
                      size: 13,
                      weight: "normal",
                    },
                    bodyFont: {
                      size: 14,
                      weight: "bold",
                    },
                    callbacks: {
                      title: (tooltipItems) => {
                        const index = tooltipItems[0].dataIndex;
                        return chartData.rawDates[index];
                      },
                      label: (tooltipItem) => {
                        return `${
                          tooltipItem.formattedValue
                        } ${label.toLowerCase()}`;
                      },
                    },
                    displayColors: false,
                  },
                },
                scales: {
                  x: {
                    grid: {
                      display: false,
                      drawBorder: false,
                    },
                    ticks: {
                      font: {
                        size: 10,
                        family: "'Inter', sans-serif",
                      },
                      color: "#64748b",
                      maxRotation: 0,
                      maxTicksLimit: 6,
                    },
                    border: {
                      display: false,
                    },
                  },
                  y: {
                    beginAtZero: true,
                    suggestedMin: suggestedMin,
                    suggestedMax: suggestedMax,
                    grid: {
                      color: "rgba(226, 232, 240, 0.7)",
                      drawBorder: false,
                    },
                    border: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 10,
                        family: "'Inter', sans-serif",
                      },
                      color: "#64748b",
                      padding: 6,
                      callback: function (value) {
                        if (value >= 1000000)
                          return (value / 1000000).toFixed(1) + "M";
                        if (value >= 1000)
                          return (value / 1000).toFixed(1) + "K";
                        return value;
                      },
                    },
                  },
                },
                layout: {
                  padding: {
                    left: 2,
                    right: 2,
                    top: 2,
                    bottom: 2,
                  },
                },
                animation: {
                  duration: 1000,
                  easing: "easeOutQuart",
                },
              }}
            />
          </div>
        </div>

        {/* Chart Footer with key metrics */}
        <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-3 gap-1">
          <div className="text-center">
            <div className="text-xs text-gray-500">Average</div>
            <div
              className="font-semibold text-xs"
              style={{ color: theme.primary }}
            >
              {values.length > 0
                ? Math.round(
                    values.reduce((a, b) => a + b, 0) / values.length
                  ).toLocaleString()
                : 0}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">Highest</div>
            <div
              className="font-semibold text-xs"
              style={{ color: theme.primary }}
            >
              {Math.max(...values).toLocaleString()}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">Points</div>
            <div
              className="font-semibold text-xs"
              style={{ color: theme.primary }}
            >
              {values.length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// View and Click chart components
const ViewChart = ({ ViewData }) => (
  <ChartComponent data={ViewData} label="Views" chartType="views" />
);

const ClickChart = ({ ClickData }) => (
  <ChartComponent data={ClickData} label="Clicks" chartType="clicks" />
);

export { ClickChart, ViewChart };
